/**
 * KAN-834: приложение менеджера (PWA) живёт под /m. Здесь его пути и правило,
 * куда отправлять пользователя, когда сессия кончилась: из приложения на
 * страницу "Попросите новую ссылку для входа", из кабинета на /login.
 */

/** Корень приложения для сравнений путей: "/m" и всё под ним (isMobileAppPath). */
export const MOBILE_ROOT = '/m';
/**
 * KAN-881: цель перехода на главную приложения. Со слешем: scope манифеста "/m/",
 * переход на "/m" лежит вне области и Chrome показывает полосу "вне приложения".
 */
export const MOBILE_HOME = '/m/';
export const MOBILE_LEADS_PATH = '/m/leads';
/** KAN-862: канбан-доска CRM (KAN-964: без вкладки, по ссылкам). */
export const MOBILE_BOARD_PATH = '/m/board';
/** KAN-964: вкладка "Встречи": встречи и звонки дня с календарём. */
export const MOBILE_MEETINGS_PATH = '/m/meetings';
/** KAN-837: свой профиль менеджера. */
export const MOBILE_PROFILE_PATH = '/m/profile';
export const MOBILE_LOGIN_EXPIRED_PATH = '/m/login-expired';
/** KAN-846: вход по логину (почта или телефон) и паролю. */
export const MOBILE_LOGIN_PATH = '/m/login';
/** KAN-845: экран "Начало работы" (первый вход и повторно из профиля). */
export const MOBILE_START_PATH = '/m/start';

export function mobileClientPath(clientId: string): string {
  return `/m/clients/${clientId}`;
}

/** KAN-851: карточка организации клиента (только чтение). */
export function mobileCompanyPath(companyId: string): string {
  return `/m/companies/${companyId}`;
}

/** Путь внутри приложения менеджера (/m и всё под ним). */
export function isMobileAppPath(pathname: string): boolean {
  return pathname === MOBILE_ROOT || pathname.startsWith(`${MOBILE_ROOT}/`);
}

/** Главная приложения: "/m/" и "/m" (старые установки открываются без слеша). */
export function isMobileHomePath(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === MOBILE_ROOT;
}

/**
 * Куда вести, когда сессия кончилась (refresh не вышел): приложение на страницу
 * "Вход закончился" (там и вход по паролю, KAN-846), кабинет на /login.
 * Без токена вовсе AuthGuard ведёт приложение сразу на форму входа /m/login.
 */
export function loginPathFor(pathname: string): string {
  return isMobileAppPath(pathname) ? MOBILE_LOGIN_EXPIRED_PATH : '/login';
}

/**
 * KAN-854: менеджер с телефона в кабинете уходит в приложение (/m). Здесь
 * признак телефона, соответствие путей кабинета путям приложения и выход
 * "остаться в кабинете" (?desktop=1, запоминается на 8 часов на устройстве, KAN-884).
 */

/** Ширина телефона (брейкпоинт md у Tailwind) или сенсорный экран в альбомной ориентации. */
const PHONE_QUERIES = ['(max-width: 767px)', '(pointer: coarse) and (max-height: 500px)'];

export function isPhoneDevice(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return PHONE_QUERIES.some((query) => window.matchMedia(query)?.matches === true);
}

const CABINET_TO_MOBILE_EXACT = new Set(['/', '/crm', '/clients', '/leads']);
const CABINET_CLIENT = /^\/clients\/([^/]+)$/;
const CABINET_CLIENT_CHAT = /^\/clients\/([^/]+)\/chat$/;
const CABINET_COMPANY = /^\/companies\/([^/]+)$/;
const COLD_CALLS_SEGMENT = 'cold-calls';

/**
 * Путь приложения для раздела кабинета, который приложение покрывает; null,
 * если раздела в приложении нет (такой путь не перенаправляется).
 */
export function mobileEquivalentPath(pathname: string): string | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (CABINET_TO_MOBILE_EXACT.has(path)) return MOBILE_HOME;
  const client = CABINET_CLIENT.exec(path);
  if (client) return client[1] === COLD_CALLS_SEGMENT ? null : mobileClientPath(client[1]);
  const chat = CABINET_CLIENT_CHAT.exec(path);
  if (chat) return chat[1] === COLD_CALLS_SEGMENT ? null : mobileClientPath(chat[1]);
  const company = CABINET_COMPANY.exec(path);
  if (company) return mobileCompanyPath(company[1]);
  return null;
}

export const DESKTOP_QUERY_PARAM = 'desktop';
export const DESKTOP_FLAG_KEY = 'smeta.forceDesktop';

/** KAN-874: CRM кабинета из приложения ("Полная CRM"): ?desktop=1, иначе телефон вернёт в /m. */
export const CABINET_CRM_PATH = `/crm?${DESKTOP_QUERY_PARAM}=1`;

/** KAN-942: главная кабинета из профиля приложения ("Полная версия"), тоже с ?desktop=1. */
export const CABINET_HOME_PATH = `/?${DESKTOP_QUERY_PARAM}=1`;

function hasDesktopParam(search: string): boolean {
  return new URLSearchParams(search).get(DESKTOP_QUERY_PARAM) === '1';
}

/** KAN-884: сколько действует выбор "кабинет на телефоне" (8 часов). */
export const DESKTOP_CHOICE_TTL_MS = 8 * 60 * 60 * 1000;

function writeStorage(kind: 'localStorage' | 'sessionStorage', value: string): void {
  try {
    window[kind].setItem(DESKTOP_FLAG_KEY, value);
  } catch {
    // storage недоступен (приватный режим): выбор действует по параметру в адресе
  }
}

function readStorage(kind: 'localStorage' | 'sessionStorage'): string | null {
  try {
    return window[kind].getItem(DESKTOP_FLAG_KEY);
  } catch {
    return null;
  }
}

function removeStorage(kind: 'localStorage' | 'sessionStorage'): void {
  try {
    window[kind].removeItem(DESKTOP_FLAG_KEY);
  } catch {
    // storage недоступен (приватный режим): снимать нечего
  }
}

/** Метка в localStorage: время выбора в мс; действует DESKTOP_CHOICE_TTL_MS. */
function hasFreshDesktopMark(): boolean {
  const raw = readStorage('localStorage');
  if (raw === null || !/^\d+$/.test(raw)) return false;
  const age = Date.now() - Number(raw);
  return age >= 0 && age < DESKTOP_CHOICE_TTL_MS;
}

/**
 * ?desktop=1 запоминает выбор "кабинет на телефоне" на 8 часов на устройстве:
 * метка времени в localStorage видна всем вкладкам (KAN-884). Флаг в
 * sessionStorage пишется для совместимости со старыми вкладками.
 */
export function rememberDesktopChoice(search: string): void {
  if (!hasDesktopParam(search)) return;
  writeStorage('localStorage', String(Date.now()));
  writeStorage('sessionStorage', '1');
}

/**
 * KAN-942: снять выбор "кабинет на телефоне" (метка в localStorage и флаг в
 * sessionStorage), чтобы "Мобильная версия" из кабинета снова вела в приложение.
 */
export function forgetDesktopChoice(): void {
  removeStorage('localStorage');
  removeStorage('sessionStorage');
}

export function isDesktopForced(search = ''): boolean {
  if (hasDesktopParam(search)) return true;
  if (hasFreshDesktopMark()) return true;
  return readStorage('sessionStorage') === '1';
}

/**
 * Куда перенаправить менеджера с телефона из кабинета; null, если остаться.
 * Роль проверяет вызывающий: сюда приходят только менеджеры.
 */
export function mobileRedirectFor(pathname: string, search = ''): string | null {
  if (isDesktopForced(search) || !isPhoneDevice()) return null;
  return mobileEquivalentPath(pathname);
}

/**
 * KAN-942: у пользователя есть приложение менеджера (пункт "Мобильная версия"
 * в меню кабинета): менеджер, владелец и любой вход со связанной записью менеджера.
 */
export function hasManagerApp(
  user: { role?: string; manager_id?: string | null } | null | undefined
): boolean {
  if (!user) return false;
  return user.role === 'MANAGER' || user.role === 'OWNER' || !!user.manager_id;
}

/**
 * KAN-943: scope манифеста PWA "/m/", адрес "/m" без слеша вне области, и Chrome
 * не предлагает установку. Для ровно "/m" возвращает "/m/" с теми же search и
 * hash, иначе null.
 */
export function mobileSlashRedirect(pathname: string, search: string, hash: string): string | null {
  if (pathname !== MOBILE_ROOT) return null;
  return `${MOBILE_HOME}${search}${hash}`;
}
